// Freebie offer key -> cart items. Static (independent of window.freeOffers)
// so selection validation and the checkout flow keep working before the offer
// map builds on DOMContentLoaded.
//
// PR B (ADR-0002 pt 4): these keys LOOK like discount codes for historical
// reasons, but they are internal offer keys only — the 6 unconditional FREE*
// codes were deactivated (EXPIRED) in Admin on 2026-07-19. The freebie itself
// is granted server-side by two automatic BXGYs (qualifying = a `plan-boxes`
// box in the cart); the theme adds the freebie LINE, the BXGY zeroes it. The
// offer set is the 3 ACTIVE-product freebies only (never re-activate archived
// products — Ryan 2026-07-19).
const FREEBIE_ITEMS_BY_CODE = {
  FREESMOKEDSALMON: [{ quantity: 2, id: "50230576513298" }],
  FREESCALLOPS: [{ quantity: 1, id: "51789329236242" }],
  FREEJERKY: [{ quantity: 2, id: "51732697055506" }],
};

// Dropped offer keys (archived Spread + Halibut products, duplicate DIP code)
// -> the active fallback offer. Old links (?freeoffer=FREEHALIBUT), stale
// cookies, and mid-journey shoppers land on a valid offer instead of a dead
// one. The fallback matches the funnel's default offer.
//
// FREEKING is here for a different reason (issue #261): the King Salmon offer
// was never launched — no Admin code, no FREEBIE_ITEMS_BY_CODE entry, and no
// BXGY covering the product — so without this entry resolveOfferKey() returns
// undefined, applyDiscount() takes the genuine-code branch, and
// writeCheckoutDiscountCookie() overwrites the shopper's real discount
// (WELCOME20 etc.) with a code Shopify does not have. Routing it to the
// fallback keeps a stale ?freeoffer=FREEKING link on a valid offer. Its
// metaobject is DRAFT today, so no card renders; the Liquid
// dropped_offer_codes filter keeps that true if it is ever published.
const DROPPED_FREEBIE_CODE_FALLBACK = {
  FREESALMONSPREAD: "FREESMOKEDSALMON",
  FREEHALIBUT: "FREESMOKEDSALMON",
  FREESALMONDIP: "FREESMOKEDSALMON",
  FREEKING: "FREESMOKEDSALMON",
};

class DiscountManager {
  constructor() {
    console.log("setting up discount manager")
    this.discountCookieName = "discount_code";
    // The shopper's freebie choice lives in its own cookie — never in Shopify's
    // discount_code, which checkout consumes (that consumption is how a chosen
    // freebie used to revert to the default on re-entry).
    this.selectionCookieName = "n2t_freebie_selection";
    this.currentDiscount = undefined;
    this.discountCookieToInfoMap = undefined;
    let manager = this;
    document.addEventListener("DOMContentLoaded", function () {

      if (window.freeOffers) {

        manager.freeOffers = window.freeOffers;
        manager.selectableDiscountCodes = manager.freeOffers.map(
          (o) => o["discount_code"]
        );
        manager.selectableDiscountCodes.push("25OFF");
        // Dropped offer keys stay "selectable" so a legacy cookie holding one
        // classifies as a (resolvable) freebie choice — not as an affiliate
        // code, which would falsely promise "your promo applies at checkout".
        Object.keys(DROPPED_FREEBIE_CODE_FALLBACK).forEach(function (key) {
          if (manager.selectableDiscountCodes.indexOf(key) === -1) {
            manager.selectableDiscountCodes.push(key);
          }
        });

        manager.discountCookieToInfoMap = window.freeOffers.reduce((map, offer) => {
          const discountCode = offer["discount_code"];
          map.set(discountCode, {
            name: offer.slug,
            displayMessage: offer.name,
            urlPath: discountCode,
            priceDescriptor: offer.box_size_price_description
          });
          return map;
        }, new Map());
      }

    });
  }


  hasAffiliateCode() {
    return sessionStorage.getItem('affiliateCode') || !this.currentDiscountIsSelectable();
  }

  getAffilateDiscount() {
    const possibleAffiliateDiscount = this.getActiveDiscountCookie();
    return this.currentDiscountIsSelectable() ? undefined : possibleAffiliateDiscount;
  }

  getActiveDiscountCookie() {
    return this.getCookie(this.discountCookieName);
  }

  getCookie(name) {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop().split(";").shift();
  }

  setDiscountCookie(name, value, days) {
    let expires = "";
    if (days) {
      const date = new Date();
      date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
      expires = `; expires=${date.toUTCString()}`;
    }
    document.cookie = `${name}=${value || ""}${expires}; path=/`;
  }

  clearDiscountCookie() {
    this.setDiscountCookie(this.discountCookieName, "", -1);
  }

  isDiscountApplied() {
    return !!this.getCookie(this.discountCookieName);
  }

  setDiscountCode(code) {
    this.currentDiscount = code;
  }

  // Freebie OFFER-KEY resolver (PR B): an active offer key resolves to
  // itself, a dropped key (archived product / dead code) resolves to the
  // active fallback offer, and anything else — WELCOME*/25OFF/affiliate/
  // unknown — resolves to undefined. The one place "is this a freebie offer
  // (and which)?" is answered.
  resolveOfferKey(code) {
    if (!code) return undefined;
    if (FREEBIE_ITEMS_BY_CODE[code]) return code;
    return DROPPED_FREEBIE_CODE_FALLBACK[code];
  }

  // "affiliate" is a UI sentinel, not a code — persisting it would shadow the
  // shopper's real affiliate discount. SESSION-scoped (no expiry days): the
  // offer choice belongs to the funnel session, not 7 days site-wide
  // (ADR-0002 pt 4).
  persistSelection(code) {
    if (!code || code === "affiliate") return;
    this.setDiscountCookie(this.selectionCookieName, code);
  }

  getPersistedSelection() {
    const value = this.getCookie(this.selectionCookieName);
    if (value && value !== "affiliate") return value;
    return this.migrateLegacyFreebieCookie();
  }

  // One-time migration (PR B): pre-PR-B funnels stored the chosen freebie in
  // Shopify's own discount_code cookie. When no theme-owned selection exists
  // but that legacy cookie holds a (possibly dropped) freebie offer key,
  // adopt the resolved offer into the session-scoped selection cookie and
  // clear the legacy cookie — the FREE* codes are EXPIRED, so leaving one
  // there only collides with Shopify's checkout and errors. Never touches
  // WELCOME*/affiliate/marketing codes (resolveOfferKey returns undefined).
  migrateLegacyFreebieCookie() {
    const legacy = this.getCookie(this.discountCookieName);
    const offerKey = this.resolveOfferKey(legacy);
    if (!offerKey) return undefined;
    this.persistSelection(offerKey);
    this.clearDiscountCookie();
    return offerKey;
  }

  // Map-validated read of the persisted choice: a stale or tampered cookie
  // value must not reach the checkout payload. Dropped offer keys resolve to
  // the active fallback first. Before window.freeOffers builds the map, the
  // static freebie table validates instead.
  getSelectedFreebie() {
    const persisted = this.getPersistedSelection();
    if (!persisted) return undefined;
    const candidate = this.resolveOfferKey(persisted) || persisted;
    const map = this.discountCookieToInfoMap;
    if (map && typeof map.has === "function") {
      return map.has(candidate) ? candidate : undefined;
    }
    return FREEBIE_ITEMS_BY_CODE[candidate] ? candidate : undefined;
  }

  // Checkout-time discount_code write. Skips the affiliate path entirely: the
  // shopper's real affiliate code is already in the cookie, and writing the
  // literal "affiliate" over it (the old applyDiscount path) voided their
  // discount at checkout. Skips empty selections for the same reason.
  // PR B: also skips freebie OFFER KEYS entirely — the freebie is granted
  // server-side by the automatic BXGY (no code involved), the FREE* codes are
  // EXPIRED, and writing them collides with Shopify's own discount_code
  // cookie. Genuine live codes (WELCOME*, winback, 100OFF…) still write.
  writeCheckoutDiscountCookie(code) {
    if (!code || code === "affiliate") return;
    if (this.resolveOfferKey(code)) return;
    this.clearDiscountCookie();
    this.setDiscountCookie(this.discountCookieName, code, 7);
  }

  // Cart items for a freebie offer key, each tagged with the hidden `_freebie`
  // line-item property so the checkout reconcile can find and replace them
  // idempotently. Applied here so every flow that adds freebies inherits it.
  // Dropped keys resolve to the active fallback offer, and the `_freebie` tag
  // carries the RESOLVED key so cart state never references a dead offer.
  freebieItemsFor(code) {
    const offerKey = this.resolveOfferKey(code);
    const items = (offerKey && FREEBIE_ITEMS_BY_CODE[offerKey]) || [];
    return items.map((item) =>
      Object.assign({}, item, { properties: { _freebie: offerKey } }),
    );
  }

  async applyCurrentDiscount() {
    await this.applyDiscount(this.currentDiscount);
  }

  async applyDiscount(code) {
      const offerKey = this.resolveOfferKey(code);
      if (offerKey) {
        // Freebie offer: session-scoped selection + the tagged cart line only.
        // No discount_code write — the automatic BXGY zeroes the line at
        // checkout; the FREE* codes are EXPIRED (PR B, ADR-0002 pt 4).
        this.persistSelection(offerKey);
        return await this.addFreebieItemsToCart(offerKey);
      }
      this.clearDiscountCookie();
      this.setDiscountCookie(this.discountCookieName, code, 7);
      return await this.addFreebieItemsToCart(code);
  }



  async addFreebieItemsToCart(code, config) {
    const items = this.freebieItemsFor(code);
    const addUrl = `${window.Shopify.routes.root}cart/add.js`;

    if (items.length > 0) {
      try {
        // Guarded, not blind-retried: /cart/add.js increments, so replaying a
        // freebie add whose response was lost would ship the shopper a second
        // free item at full COGS — invisible to them, a pure margin loss to us.
        // cartAdd reconciles against the server's cart before it replays anything.
        const options = {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ items: items }),
        };
        const cfg = Object.assign({ step: "freebie-add" }, config);
        const guardedAdd =
          cfg.cartAdd || (window.CartAddGuard && window.CartAddGuard.cartAdd);

        if (!guardedAdd) {
          // Guard asset missing: one attempt, no retry. A missed freebie is
          // recoverable; a duplicated one is shipped product we never sold.
          const response = await fetch(addUrl, options);
          if (!response || !response.ok) {
            throw new Error(`HTTP ${response ? response.status : "no response"}`);
          }
          return response;
        }

        const result = await guardedAdd(addUrl, options, cfg);
        return result.alreadyApplied ? null : result.response;
      } catch (error) {
        console.error("Error adding freebie items to cart:", error);
      }
    }
  }


  newDiscountIsSelectable() {
      if (!this.selectableDiscountCodes || Object.keys(this.selectableDiscountCodes).length === 0) {
      return true;
    }
        var discountIsSelectable = false;
    discountIsSelectable = !this.currentDiscount || this.selectableDiscountCodes.includes(this.currentDiscount);
    return discountIsSelectable;
  }
  currentDiscountIsSelectable() {

    var discountIsSelectable = false;
    var codeCookieValue = this.getCookie("discount_code");
    discountIsSelectable = !codeCookieValue || this.selectableDiscountCodes.includes(codeCookieValue);
    return discountIsSelectable;
  }
}
if (typeof process === 'undefined' || !process.versions || !process.versions.node) {
  window.discountManager = window.discountManager || new DiscountManager();
}

// Node-only test export.
if (
  typeof module === 'object' && module !== null &&
  typeof module.exports === 'object' &&
  typeof process !== 'undefined' && process.versions != null && process.versions.node != null
) {
  module.exports = { DiscountManager };
}
